import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { useShellTitle } from '../components/layout/ShellTitleContext';
import { useThemeStore } from '../stores/themeStore';
import type { Theme } from '../stores/themeStore';
import { useAuthStore } from '../stores/authStore';
import {
  listAiModels,
  listVoices,
  saveSettings,
} from '../services/settingsApi';
import type { AiModel, TtsVoice } from '../services/settingsApi';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';

const THEMES: { id: Theme; label: string }[] = [
  { id: 'paper', label: 'Paper · Coral' },
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold text-ws-ink mb-3">{title}</h2>
      {children}
    </Card>
  );
}

export default function Settings() {
  useShellTitle('Settings');
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const { user, setUser } = useAuthStore();

  const [models, setModels] = useState<AiModel[]>([]);
  const [voices, setVoices] = useState<TtsVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [voice, setVoice] = useState<string>(user?.preferred_voice || '');
  const [model, setModel] = useState<string>(user?.preferred_ai_model || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([listAiModels(), listVoices()]).then(([m, v]) => {
      if (cancelled) return;
      if (m.status === 'fulfilled') setModels(m.value);
      if (v.status === 'fulfilled') setVoices(v.value);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const updated = await saveSettings({
        preferred_voice: voice || null,
        preferred_ai_model: model || null,
        preferred_theme: theme,
      });
      setUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ws-page">
      <h1 className="text-xl font-semibold text-ws-ink mb-5">Settings</h1>

      <div className="flex flex-col gap-4">
        {/* Theme */}
        <Section title="Appearance">
          <div className="flex flex-wrap gap-2">
            {THEMES.map((t) => (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                  theme === t.id
                    ? 'border-ws-accent bg-ws-accent-quiet text-ws-accent-ink font-medium'
                    : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                {theme === t.id && <Check size={14} />}
                {t.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Voice */}
        <Section title="Narration voice">
          {loading ? (
            <Spinner label="Loading voices…" />
          ) : voices.length === 0 ? (
            <p className="text-sm text-ws-ink-3">No voices available.</p>
          ) : (
            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              className="w-full rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
            >
              <option value="">Default voice</option>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label || v.id}
                  {v.language ? ` (${v.language})` : ''}
                </option>
              ))}
            </select>
          )}
        </Section>

        {/* AI model */}
        <Section title="AI model">
          {loading ? (
            <Spinner label="Loading models…" />
          ) : models.length === 0 ? (
            <p className="text-sm text-ws-ink-3">No models available.</p>
          ) : (
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full rounded-md border border-ws-border bg-ws-surface px-3 py-2 text-sm text-ws-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
            >
              <option value="">Default model</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label || m.id}
                  {m.provider ? ` — ${m.provider}` : ''}
                </option>
              ))}
            </select>
          )}
        </Section>

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} loading={saving}>
            Save changes
          </Button>
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm text-ws-success">
              <Check size={15} /> Saved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
